import React from 'react';
import { Image, Pressable, View } from 'react-native';

import { CHECKLIST_ITEM_TYPES } from '../../../common/utils/checklist/checklistItems';
import { Text } from '../../components';
import { TextInput } from '../../components/input';
import Switch from '../../components/switch/Switch';
import { SecondaryButton, YesNo } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import OptionSelect from '../roster/components/OptionSelect';
import { createChecklistFormViewStyles } from './ChecklistFormView.styles';
import { ITEM_CONTROL, MAX_PHOTOS_PER_ITEM, getItemControl } from './checklistFormHelpers';

/**
 * ChecklistItemControl: the answer control for one checklist item
 * (getItemControl): toggle, Yes/No, number, pills, choices, select, option
 * switches, text or photos.
 * Props: item, value, onAnswer(itemId, value), onAddPhoto(itemId),
 * onRemovePhoto(itemId, link), processingMedia, isCapturingPhoto (this item's photos are uploading),
 * isReadOnly (submitted checklist: photos show without Remove or Add photo)
 */
const ChecklistItemControl = ({
	item,
	value,
	onAnswer,
	onAddPhoto,
	onRemovePhoto,
	processingMedia,
	isCapturingPhoto,
	isReadOnly,
}) => {
	const styles = useThemedStyles(createChecklistFormViewStyles);

	const renderYesNo = () => {
		if (item.type === CHECKLIST_ITEM_TYPES.CHECKBOX) {
			return (
				<YesNo
					value={typeof value === 'boolean' ? value : null}
					onChange={nextValue => onAnswer(item.id, nextValue)}
					accessibilityLabel={item.label}
				/>
			);
		}

		const [yesOption, noOption] = item.options;
		return (
			<YesNo
				value={value === yesOption ? true : value === noOption ? false : null}
				onChange={nextValue => onAnswer(item.id, nextValue ? yesOption : noOption)}
				yesLabel={yesOption}
				noLabel={noOption}
				accessibilityLabel={item.label}
			/>
		);
	};

	const renderPills = () => (
		<View style={styles.pills} accessibilityRole="radiogroup" accessibilityLabel={item.label}>
			{item.options.map((option) => {
				const isSelected = value === option;
				return (
					<Pressable
						key={option}
						onPress={() => onAnswer(item.id, option)}
						accessibilityRole="radio"
						accessibilityLabel={`${item.label}: ${option}`}
						accessibilityState={{ selected: isSelected, checked: isSelected }}
						style={({ pressed }) => [styles.pill, isSelected && styles.pillSelected, pressed && styles.pressed]}
					>
						<Text style={[styles.pillText, isSelected && styles.pillTextSelected]}>{option}</Text>
					</Pressable>
				);
			})}
		</View>
	);

	const renderChoices = () => (
		<View style={styles.choices} accessibilityRole="radiogroup" accessibilityLabel={item.label}>
			{item.options.map((option) => {
				const isSelected = value === option;
				return (
					<Pressable
						key={option}
						onPress={() => onAnswer(item.id, option)}
						accessibilityRole="radio"
						accessibilityLabel={`${item.label}: ${option}`}
						accessibilityState={{ selected: isSelected, checked: isSelected }}
						style={({ pressed }) => [styles.choice, isSelected && styles.pillSelected, pressed && styles.pressed]}
					>
						<Text style={[styles.choiceText, isSelected && styles.pillTextSelected]}>{option}</Text>
					</Pressable>
				);
			})}
		</View>
	);

	const renderCheckboxGroup = () => {
		const selectedValues = Array.isArray(value) ? value : [];
		return (
			<View style={styles.optionList}>
				{(item.options || []).map(option => (
					<View key={option} style={styles.optionRow}>
						<Text style={styles.optionText}>{option}</Text>
						<Switch
							value={selectedValues.includes(option)}
							onValueChange={(isSelected) => {
								const nextValues = isSelected
									? [...selectedValues, option]
									: selectedValues.filter(existingOption => existingOption !== option);
								onAnswer(item.id, nextValues);
							}}
							accessibilityLabel={`${item.label}: ${option}`}
						/>
					</View>
				))}
			</View>
		);
	};

	const renderPhotos = () => {
		const photoLinks = Array.isArray(value) ? value : [];
		return (
			<>
				{
					photoLinks.length > 0 && (
						<View style={styles.photoRow}>
							{
								photoLinks.map(link => (
									<Pressable
										key={link}
										onPress={() => onRemovePhoto(item.id, link)}
										accessibilityRole="button"
										accessibilityLabel="Remove photo"
										style={styles.photo}
									>
										<Image source={{ uri: link }} style={styles.photoThumbnail} />
										{!isReadOnly && <Text style={styles.photoRemoveHint}>Remove</Text>}
									</Pressable>
								))
							}
						</View>
					)
				}
				{!isReadOnly && (
					<SecondaryButton
						label={photoLinks.length ? 'Add another photo' : 'Add photo'}
						icon="camera"
						onPress={() => onAddPhoto(item.id)}
						disabled={processingMedia || photoLinks.length >= MAX_PHOTOS_PER_ITEM}
						loading={isCapturingPhoto}
					/>
				)}
			</>
		);
	};

	const renderControl = () => {
		switch (getItemControl(item)) {
			case ITEM_CONTROL.TOGGLE:
				return (
					<Switch
						value={Boolean(value)}
						onValueChange={nextValue => onAnswer(item.id, nextValue)}
						accessibilityLabel={item.label}
					/>
				);
			case ITEM_CONTROL.YES_NO:
				return renderYesNo();
			case ITEM_CONTROL.NUMBER:
				return (
					<TextInput
						value={value === undefined || value === null ? '' : String(value)}
						onChange={nextValue => onAnswer(item.id, nextValue)}
						placeholder={item.placeholder || ''}
						keyboardType="numeric"
						accessibilityLabel={item.label}
						style={styles.numberInput}
					/>
				);
			case ITEM_CONTROL.PILLS:
				return renderPills();
			case ITEM_CONTROL.CHOICES:
				return renderChoices();
			case ITEM_CONTROL.SELECT:
				return (
					<OptionSelect
						label={item.label}
						options={item.options.map(option => ({ label: option, value: option }))}
						value={value ?? null}
						onChange={nextValue => onAnswer(item.id, nextValue)}
						placeholder="Select"
						showLabel={false}
					/>
				);
			case ITEM_CONTROL.CHECKBOX_GROUP:
				return renderCheckboxGroup();
			case ITEM_CONTROL.TEXT:
				// A submitted answer shows in full; a text box would clip it and scroll inside.
				if (isReadOnly) {
					return (
						<View style={styles.readOnlyField}>
							<Text style={styles.readOnlyFieldText}>{value || '—'}</Text>
						</View>
					);
				}
				return (
					<TextInput
						value={value || ''}
						onChange={nextValue => onAnswer(item.id, nextValue)}
						placeholder={item.placeholder || ''}
						multiline={true}
						maxLength={500}
						accessibilityLabel={item.label}
						style={styles.multilineInput}
					/>
				);
			case ITEM_CONTROL.PHOTO:
				return renderPhotos();
			default:
				return null;
		}
	};

	return renderControl();
};

export default ChecklistItemControl;
